/**
 * Shared filter-catalogue types — same payload shape on
 * `/account/searches/filters` and `/listings/filters`. Field names mirror the
 * wire format (snake_case): the catalogue `key` is the canonical id; the
 * camelCase variant of a key is what goes in `/listings` query params.
 */

/** One choice of a `select` filter (e.g. a saved search for `search_id`). */
export interface FilterOption {
  value: string
  label: string
  commune_code?: string | null
  radius_km?: number | null
  latitude?: number | null
  longitude?: number | null
}

/** Normalise une entrée `valid_values` (primitive OU {value, label}) en {value, label}. */
export function toFilterOption(v: string | number | FilterOption): { value: string; label: string } {
  if (typeof v === 'object' && v !== null) return { value: String(v.value), label: v.label || String(v.value) }
  return { value: String(v), label: String(v) }
}

export interface FilterDef {
  key: string
  type: string
  label: string
  description?: string | null
  /** Soit des primitives (forme historique), soit des {value, label}. */
  valid_values?: (string | number | FilterOption)[]
  options?: FilterOption[]
  min_value?: number | null
  max_value?: number | null
  unit?: string | null
  default_value?: unknown
  /** The feed can actually apply this filter (data present in the snapshot). */
  applicable?: boolean
  /** The current subscription unlocks this filter. */
  enabled?: boolean
}

export interface FilterCategory {
  key: string
  label: string
  description: string | null
  filters: FilterDef[]
}

export interface FilterCatalog {
  tier: string
  filter_level: 'none' | 'basic' | 'fine'
  filters_count: number
  categories: FilterCategory[]
}

/**
 * Usable on GET /listings — the subscription unlocks it. `applicable` only
 * says whether the current snapshot holds data for it; the server accepts the
 * filter either way, so the UI exposes it regardless.
 */
export function isFilterUsable(f: FilterDef): boolean {
  return f.enabled === true
}

/** Minuscule sans accents, pour la recherche de filtre. */
function norm(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

/** Un filtre matche par son label ou par une de ses valeurs possibles. */
function filterMatches(f: FilterDef, q: string): boolean {
  if (norm(f.label).includes(q)) return true
  return (f.valid_values ?? []).some((raw) => {
    const o = toFilterOption(raw)
    return norm(o.label).includes(q) || norm(o.value).includes(q)
  })
}

/** Restreint les catégories/filtres au texte recherché (DPE = tout ou rien). */
export function searchCategories(categories: FilterCategory[], query: string): FilterCategory[] {
  const q = norm(query.trim())
  if (!q) return categories
  return categories
    .map((c) => {
      const catHit = norm(c.label).includes(q)
      if (c.key === 'energy_class') {
        const hit = catHit || c.filters.some((f) => filterMatches(f, q))
        return { ...c, filters: hit ? c.filters : [] }
      }
      const filters = catHit ? c.filters : c.filters.filter((f) => filterMatches(f, q))
      return { ...c, filters }
    })
    .filter((c) => c.filters.length > 0)
}

/** First filter of the catalogue matching `key`, across every category. */
export function findFilterDef(catalog: FilterCatalog, key: string): FilterDef | undefined {
  for (const cat of catalog.categories) {
    const def = cat.filters.find((f) => f.key === key)
    if (def) return def
  }
  return undefined
}

/**
 * Libellés d'options réécrits côté front, sur les deux catalogues :
 * `periode_construction = annee_inconnu` désigne une période de construction
 * inconnue (≈ 0 DPE), et non plus une année exacte inconnue.
 */
const OPTION_LABELS: Record<string, Record<string, string>> = {
  periode_construction: { annee_inconnu: 'Période inconnue' },
}

/** Applique `OPTION_LABELS` au catalogue reçu (en place, à la frontière API). */
export function relabelOptions(categories: FilterCategory[]): void {
  for (const cat of categories) {
    for (const def of cat.filters) {
      const labels = OPTION_LABELS[def.key]
      if (!labels || !def.valid_values) continue
      def.valid_values = def.valid_values.map((raw) => {
        const o = toFilterOption(raw)
        const label = labels[o.value]
        return label ? { ...o, label } : raw
      })
    }
  }
}

/**
 * Libellé d'une paire de bornes sans son suffixe : "Surface minimale" / "Prix min." /
 * "Émissions GES maximales" → "Surface" / "Prix" / "Émissions GES".
 */
export function rangeLabel(label: string): string {
  return label.replace(/\s+((min|max)(ima(le?s?|ux)|imum|\.)?|à partir du|jusqu'au)\s*$/i, '') || label
}
